import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

interface AiTypingContextValue {
  isTyping: (conversationId: string) => boolean;
  addTyping: (conversationId: string) => void;
  removeTyping: (conversationId: string) => void;
}

const noop = () => {};

const AiTypingContext = createContext<AiTypingContextValue>({
  isTyping: () => false,
  addTyping: noop,
  removeTyping: noop
});

export const AI_TYPING_SAFETY_TIMEOUT_MS = 30_000;

export const AiTypingProvider = ({ children }: { children: ReactNode }) => {
  const [typingSet, setTypingSet] = useState<Set<string>>(() => new Set());
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  const removeTyping = useCallback((conversationId: string) => {
    setTypingSet((previous) => {
      if (!previous.has(conversationId)) return previous;
      const next = new Set(previous);
      next.delete(conversationId);
      return next;
    });
    const existing = timeoutsRef.current.get(conversationId);
    if (existing) {
      clearTimeout(existing);
      timeoutsRef.current.delete(conversationId);
    }
  }, []);

  const addTyping = useCallback(
    (conversationId: string) => {
      setTypingSet((previous) => {
        if (previous.has(conversationId)) return previous;
        const next = new Set(previous);
        next.add(conversationId);
        return next;
      });

      const existing = timeoutsRef.current.get(conversationId);
      if (existing) clearTimeout(existing);

      const handle = setTimeout(
        () => removeTyping(conversationId),
        AI_TYPING_SAFETY_TIMEOUT_MS
      );
      timeoutsRef.current.set(conversationId, handle);
    },
    [removeTyping]
  );

  const isTyping = useCallback(
    (conversationId: string) => typingSet.has(conversationId),
    [typingSet]
  );

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      for (const handle of timeouts.values()) clearTimeout(handle);
      timeouts.clear();
    };
  }, []);

  const value = useMemo<AiTypingContextValue>(
    () => ({ isTyping, addTyping, removeTyping }),
    [isTyping, addTyping, removeTyping]
  );

  return (
    <AiTypingContext.Provider value={value}>
      {children}
    </AiTypingContext.Provider>
  );
};

export const useAiTyping = () => useContext(AiTypingContext);
